<template>
  <div class="subscription-form-container">
    <div class="section-container">
      <substrate-mount>
        <div class="textarea-composite-container">
          <input-textarea
            v-model="bill.description"
            input-id="bill-description"
            :input-name="$t('bill.ticket.description.label')"
            :input-title="$t('bill.ticket.description.label')"
            :input-placeholder="$t('bill.ticket.description.placeholder')"
            :max-length="510"
            :required="false"
            :rows="1"
            composite
          />
          <attachment-image
            :link="bill.attachment.link"
            input-id="bill.attachment"
            type="secondary"
            composite
            composite-textarea
            @loading="onImageLoading"
            @uploaded="data => bill.attachment.setData(data)"
            @reset="bill.attachment.reset()"
          />
        </div>
        <div class="switcher-container">
          <cs-switcher
            v-model="bill.isMulti"
            :label="$t('allow.multiple.payments')"
          />
        </div>
      </substrate-mount>
    </div>

    <div
      v-for="(tariff, idx) in bill.billTariffs"
      :key="`tariff-${idx}`"
      class="section-container"
    >
      <template v-if="isDesktop">
        <form-movement-control
          v-if="isSeveralTariffsAvailable"
          :inactive-up="isInactiveMovementUp(idx)"
          :inactive-down="isInactiveMovementDown(idx)"
          @movementUp="onMovementUp(idx)"
          @movementDown="onMovementDown(idx)"
        />
        <form-delete-icon
          v-if="isSeveralTariffsAvailable"
          @click="onRemoveTariff(idx)"
        />
      </template>
      <substrate-mount>
        <div class="input-container">
          <input-textarea
            v-model="tariff.name"
            :input-id="`tariff-name-${idx}`"
            :input-name="$t('bill.ticket.variant.name.label')"
            :input-title="$t('bill.ticket.variant.name.label')"
            :input-placeholder="$t('bill.ticket.variant.name.placeholder')"
            :max-length="255"
            :required="false"
            :rows="1"
          />
        </div>
        <div class="input-container">
          <input-textarea
            v-model="tariff.description"
            :input-name="$t('bill.ticket.variant.description.label')"
            :input-title="$t('bill.ticket.variant.description.label')"
            :input-placeholder="$t('bill.ticket.variant.description.placeholder')"
            :input-id="`tariff-description-${idx}`"
            :max-length="255"
            :required="false"
            :rows="1"
          />
        </div>
        <div class="textarea-composite-container">
          <input-textarea
            :value="tariff.arrangement.template.name"
            :input-id="`tariff-arrangement-template-name-${idx}`"
            :input-name="$t('bill.ticket.template.field.label')"
            :input-title="$t('bill.ticket.template.field.label')"
            :input-placeholder="$t('bill.ticket.template.field.placeholder')"
            :show-counter="false"
            :max-length="255"
            :required="false"
            :rows="1"
            composite
            readonly
            @click="handleAttachmentById(`attachment-${idx}`)"
          >
            <help-link slot="link" source="https://help.capusta.space/ticket_template" />
          </input-textarea>
          <attachment-image
            :ref="`attachment-${idx}`"
            :link="tariff.arrangement.template.link"
            :input-id="`tariff-arrangement-template-link-${idx}`"
            type="secondary"
            composite
            composite-textarea
            upload-type="ticket"
            accept="application/vnd.ms-word.document.macroEnabled.12, application/vnd.openxmlformats-officedocument.wordprocessingml.document, application/vnd.openxmlformats-officedocument.wordprocessingml.template, application/vnd.ms-word.template.macroEnabled.12"
            @loading="onImageLoading"
            @uploaded="data => tariff.arrangement.template.setData(data)"
            @reset="tariff.arrangement.template.reset()"
          />
        </div>
        <template v-if="bill.isMulti">
          <div class="switcher-container">
            <cs-switcher
              v-model="tariff.arrangement.isCanCountBundle"
              :label="$t('bill.ticket.variant.count.bundle.switcher')"
            />
          </div>
          <div class="grid-two-columns">
            <div class="input-container">
              <input-number
                v-if="tariff.arrangement.isCanCountBundle"
                ref="countPerPayment"
                v-model="tariff.arrangement.countPerPayment"
                :input-name="$t('bill.ticket.variant.count.per.payment.label')"
                :input-title="$t('bill.ticket.variant.count.per.payment.label')"
                :input-placeholder="$t('bill.ticket.variant.count.per.payment.placeholder')"
                :input-id="`tariff-arrangement-count-per-${idx}`"
                :show-counter="false"
                :max-length="3"
                :min-value="1"
                :max-value="tariff.arrangement.getMaxCountPerPayment()"
                :required="false"
                only-integer
              >
                <help-link slot="link" source="https://help.capusta.space/tickets_for_one" />
              </input-number>
              <input-number
                v-else
                v-model="tariff.arrangement.countBundle"
                :input-name="$t('bill.ticket.variant.count.bundle.label')"
                :input-title="$t('bill.ticket.variant.count.bundle.label')"
                :input-placeholder="$t('bill.ticket.variant.count.bundle.placeholder')"
                :input-id="`tariff-arrangement-count-bundle-${idx}`"
                :show-counter="false"
                :max-length="3"
                :min-value="1"
                :max-value="tariff.arrangement.getMaxCountPerPayment()"
                only-integer
                required
              >
                <help-link slot="link" source="https://help.capusta.space/tickets_for_payment" />
              </input-number>
            </div>
            <div class="input-container">
              <input-number
                v-model="tariff.arrangement.count"
                :input-name="$t('bill.ticket.variant.count.label')"
                :input-title="$t('bill.ticket.variant.count.label')"
                :input-placeholder="$t('bill.ticket.variant.count.placeholder')"
                :input-id="`tariff-arrangement-count-${idx}`"
                :show-counter="false"
                :max-length="6"
                :min-value="1"
                :max-value="10000"
                :required="false"
                only-integer
              >
                <help-link slot="link" source="https://help.capusta.space/tickets_on_sale" />
              </input-number>
            </div>
          </div>
        </template>
        <template v-else>
          <div class="input-container">
            <input-number
              v-model="tariff.arrangement.countBundle"
              :input-name="$t('bill.ticket.variant.count.bundle.label')"
              :input-title="$t('bill.ticket.variant.count.bundle.label')"
              :input-placeholder="$t('bill.ticket.variant.count.bundle.placeholder')"
              :input-id="`tariff-arrangement-count-bundle-${idx}`"
              :show-counter="false"
              :max-length="3"
              :min-value="1"
              :max-value="100"
              only-integer
              required
            >
              <help-link slot="link" source="https://help.capusta.space/tickets_for_payment" />
            </input-number>
          </div>
        </template>
        <labeled-input
          v-model="tariff.amount"
          :label="$t('amount.for.pay', { getCurrencySign: currency.sign })"
          :placeholder="`0 ${currency.sign}`"
          :error="$v.bill.billTariffs.$each[idx].amount.$error"
          :force-replace="[
            [/[^0-9\.\,]+/g, ''],
            [/^\./g, ''],
            [/\,/, '\.'],
          ]"
          :force-format="[$formatInputAmount({
            significantZeros: currency.significant_zeros,
            decimalsSeparator: '.',
            thousandSeparator: ' ',
          })]"
          @focus="$v.bill.billTariffs.$each[idx].amount.$reset"
          @blur="$v.bill.billTariffs.$each[idx].amount.$touch"
        />
        <template v-if="!isDesktop">
          <div v-if="isSeveralTariffsAvailable" class="controls-container">
            <form-movement-control
              :inactive-up="isInactiveMovementUp(idx)"
              :inactive-down="isInactiveMovementDown(idx)"
              @movementUp="onMovementUp(idx)"
              @movementDown="onMovementDown(idx)"
            />
            <form-delete-icon @click="onRemoveTariff(idx)" />
          </div>
        </template>
      </substrate-mount>
    </div>

    <div v-if="!isExceededTariffsCount" class="panel-container">
      <capusta-panel
        icon="circle-plus"
        :description="$t('add.variant')"
        @click="onAddTariff"
      />
    </div>

    <div class="button-primary-container">
      <cs-button :disabled="!isValid" @click="onSubmit">
        {{ $t('get.link') }}
      </cs-button>
    </div>

    <div class="button-secondary-container">
      <cs-button type="secondary" :disabled="!isValid" @click="onSubmitWithQrCode">
        <span class="icon">
          <cs-icon name="qr-code" size="extra-medium" />
        </span>
        {{ $t('get.qr') }}
      </cs-button>
    </div>
  </div>
</template>

<script>
import { mapState, mapGetters } from 'vuex';

import attachmentMixin from 'lib/mixins/attachmentMixin';

import { isLessMaxAmount, isMoreMinAmount } from '@/components/inputElements/CustomValidators';

import InputTextarea from '@/components/inputElements/InputTextarea';
import InputNumber from '@/components/inputElements/InputNumber';

import SubstrateMount from '@/components/capusta/SubstrateMount';
import FormDeleteIcon from '@/components/capusta/FormDeleteIcon';
import FormMovementControl from '@/components/capusta/FormMovementControl';

import CapustaPanel from 'modules/CapustaLayout/CapustaPanel';
import AttachmentImage from 'modules/AttachmentImage';

import HelpLink from 'molecules/HelpLink';
import LabeledInput from 'molecules/common/LabeledInput';

import Attachment from 'lib/project/classes/Attachment';
import TicketForm from '../classes/TicketForm';
import BillTicket from '../classes/BillTicket';

import { MAX_TARIFFS } from '../const';

export default {
  name: 'TicketInvoicing',
  mixins: [
    attachmentMixin,
  ],
  components: {
    InputTextarea,
    InputNumber,
    SubstrateMount,
    FormDeleteIcon,
    FormMovementControl,
    CapustaPanel,
    AttachmentImage,
    HelpLink,
    LabeledInput,
  },
  props: {
    transaction: Object,
  },
  mounted() {
    this.fillingData();
  },
  data() {
    return {
      bill: new TicketForm,
    };
  },
  validations() {
    return {
      bill: {
        billTariffs: {
          $each: {
            amount: {
              minValue: isMoreMinAmount(this.limits.min_amount, this.currency),
              maxValue: isLessMaxAmount(this.limits.max_amount, 0, this.currency),
            },
          },
        },
      },
    };
  },
  computed: {
    currency() {
      return this.PROJECT_INFO.currency;
    },
    isExceededTariffsCount() {
      return this.bill.billTariffs.length >= MAX_TARIFFS;
    },
    isSeveralTariffsAvailable() {
      return this.bill.billTariffs.length > 1;
    },
    isValid() {
      if (this.submittingDisabled) {
        return false;
      }

      return this.isValidAmount && this.isValidCount && this.isValidCountBundle && this.isValidCountPerPayment;
    },
    isValidAmount() {
      const { billTariffs } = this.bill;

      if (billTariffs.every(tariff => this.$validate.isEmptyString(tariff.amount))) {
        return true;
      }

      return !this.$v.bill.$invalid;
    },
    isValidCount() {
      const { isMulti, billTariffs } = this.bill;

      if (!isMulti) {
        return true;
      }

      const isValidVariants = billTariffs.every(tariff => {
        const { arrangement: { count } } = tariff;

        if (this.$validate.isEmptyString(count)) {
          return true;
        }

        const value = count.replace(/\s/g, '');

        return Number(value) > 0 && Number(value) <= 10000;
      });

      return isValidVariants;
    },
    isValidCountPerPayment() {
      const { isMulti, billTariffs } = this.bill;

      if (!isMulti) {
        return true;
      }

      const isValidVariants = billTariffs.every(tariff => {
        const { arrangement } = tariff;
        const { countPerPayment, isCanCountBundle } = arrangement;

        if (this.$validate.isEmptyString(countPerPayment)) {
          return true;
        }

        const value = Number(countPerPayment);
        const limit = arrangement.getMaxCountPerPayment();

        if (isCanCountBundle) {
          return value > 0 && value <= limit;
        }

        return true;
      });

      return isValidVariants;
    },
    isValidCountBundle() {
      const { isMulti, billTariffs } = this.bill;

      const isValidVariants = billTariffs.every(tariff => {
        const { arrangement } = tariff;
        const { countBundle, isCanCountBundle } = arrangement;

        const value = Number(countBundle);
        const limit = arrangement.getMaxCountPerPayment();

        if (!isMulti) {
          return value > 0;
        }

        if (isCanCountBundle) {
          return true;
        }

        return value > 0 && value <= limit;
      });

      return isValidVariants;
    },
    ...mapState(['PROJECT_INFO']),
    ...mapGetters(['isDesktop']),
    ...mapGetters('bills', ['limits']),
  },
  methods: {
    onAddTariff() {
      if (this.isExceededTariffsCount) {
        return;
      }

      this.bill.addTariff();
    },
    onRemoveTariff(idx) {
      this.bill.deleteTariff(idx);
      // TODO: багфикс для обновления $refs и корректной валидации
      this.$nextTick(() => {
        this.bill.billTariffs = this.bill.billTariffs.map(tariff => tariff);
      });
    },
    isInactiveMovementUp(idx) {
      return idx === 0;
    },
    isInactiveMovementDown(idx) {
      return idx === this.bill.billTariffs.length - 1;
    },
    onMovementUp(idx) {
      if (idx <= 0) {
        return;
      }

      this.bill.tariffUp(idx);
    },
    onMovementDown(idx) {
      if (idx >= this.bill.billTariffs.length - 1) {
        return;
      }

      this.bill.tariffDown(idx);
    },
    onSubmitWithQrCode() {
      this.onSubmit(true);
    },
    onSubmit(useQrCode = false) {
      if (!this.isValid) {
        this.$alert({
          type: 'warning',
          text: 'mistakes.in.form',
        });
        return;
      }

      this.$emit('submit', {
        bill: this.bill.getData(),
        qrCode: useQrCode,
      });
    },
    async fillingData() {
      if (!this.transaction) {
        this.bill.currency = this.currency;
        return false;
      }

      this.bill.isMulti = this.transaction.multiBill;
      this.bill.description = this.transaction.description || null;
      this.bill.attachment = new Attachment(this.transaction.attachment);
      this.bill.currency = this.transaction.currency;

      this.$nextTick(async () => {
        this.bill.billTariffs = await this.transaction.billTariffs
          .map(tariff => new BillTicket(tariff, {
            isMulti: this.bill.isMulti,
            currency: this.transaction.currency,
          }));
      });
    },
    handleAttachmentById(refId) {
      const list = this.$refs[refId];

      if (this.$validate.isUndefined(list)) {
        return;
      }

      if (this.$validate.isEmptyArray(list)) {
        return;
      }

      list[0].$el.querySelectorAll('[type="file"]')[0].click();
    },
  },
};
</script>

<style lang="sass" scoped>
  .section-container
    position: relative
    margin-bottom: 20px

    & + .button-primary-container
      margin-top: 40px

  .input-container
    .input,
    .textarea
      margin: 0

      a
        height: auto
        margin: 0
        padding: 0
        align-self: auto
        text-decoration: none
        position: absolute
        right: 20px

        &:after
          content: none

  .textarea-composite-container
    position: relative
    display: flex
    align-items: center

    .textarea
      margin: 0

      a
        height: auto
        margin: 0
        padding: 0
        align-self: auto
        text-decoration: none
        position: absolute
        right: 80px

        &:after
          content: none

  .panel-container
    margin: 40px 0
    padding: 0 20px

  .controls-container
    display: flex
    justify-content: space-between

  .button-primary-container
    display: flex
    justify-content: center
    margin-bottom: 20px

  .button-secondary-container
    display: flex
    justify-content: center

    & .icon
      margin-right: 10px

  .grid-two-columns
    display: grid
    grid-template-columns: 1fr 1fr
    grid-gap: 20px
</style>
